// Pnakotic Manuscript pure helper. No React, no Three.js.
//
// ── Codex (Pnakotic Manuscript, Artifact) ──
//   Bearer has "Tap → Reveal your topmost spell and draw it. Bearer
//   takes damage equal to that card's cost."
//
// The activated ability lives on the artifact, but the "Tap" cost taps
// the BEARER (the equipped unit), not the artifact. An equipped relic
// has no tap state of its own, so the cost is paid by the bearer. The
// entry therefore carries no schema tap cost; the tap is applied here
// against the bearer (gated on the bearer being untapped), then the
// bearer's spellbook top is revealed + drawn through the unified draw
// funnel, and finally that card's printed cost is dealt to the bearer.
//
// Host adapter contract:
//   findBearer(artifact): card | null. The equipped host carrying
//     the artifact (a Minion or the Avatar), or null when the artifact
//     sits on the field unattached (no bearer ⇒ ability fizzles).
//   isTapped(card): boolean
//   tapBearer(card): void. Taps the bearer (unified tap).
//   spellbookTopFor(controller): { instance, fullCard } | null
//   drawSpellFor(controller): void. Draw the top spell into hand.
//   getCardCost(fullCard): { mana }
//   damageTarget(card, amount, opts): void
//   logAction(key, values?): void

import { msg } from '../../../../logMessage';

/**
 * Resolve Pnakotic Manuscript's bearer ability.
 *
 * @param {object} artifact     the Pnakotic Manuscript instance
 * @param {string} controller   'p1' | 'p2'. The bearer's controller
 * @param {object} host
 * @returns {boolean} true when the ability resolved (cost paid)
 */
export function applyRevealDrawDamageBearer(artifact, controller, host) {
  const bearer = host.findBearer(artifact);
  if (!bearer) {
    host.logAction('{source}: no bearer to draw for', { source: artifact.name || msg('Pnakotic Manuscript') });
    return false;
  }
  if (host.isTapped(bearer)) {
    host.logAction('{source}: {target} is already tapped', { source: artifact.name || msg('Pnakotic Manuscript'), target: bearer.name || msg('the bearer') });
    return false;
  }
  const top = host.spellbookTopFor(controller);
  if (!top || !top.fullCard) {
    host.logAction('{source}: spellbook is empty', { source: artifact.name || msg('Pnakotic Manuscript') });
    return false;
  }
  host.tapBearer(bearer);
  const cost = host.getCardCost(top.fullCard).mana || 0;
  host.logAction('{source}: reveals {card} (cost {n})', {
    source: artifact.name || msg('Pnakotic Manuscript'),
    card: top.fullCard.name || msg('a spell'),
    n: cost,
  });
  host.drawSpellFor(controller);
  if (cost > 0) {
    host.damageTarget(bearer, cost, {
      sourceLabel: artifact.name || 'Pnakotic Manuscript',
      source: artifact,
      log: true,
      sweep: true,
      floatingDamage: true,
    });
  }
  return true;
}
